import { Link } from "react-router";
import cx from "classnames";
import { getRunStatusWithRetries } from "src/business-utils/runStatus.utils";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useSearchStorage } from "src/storage/useSearchStorage";
import type { PitPath, TaskRunPath } from "src/utils/appRoutes/appRoutes.types";
import WhiteTooltip from "@components/general/WhiteTooltip/WhiteTooltip";
import RunStatusBar from "./components/RunStatusBar/RunStatusBar";
import {
  RUN_STATUS_BAR_HEIGHT_CLASSNAME,
  RUN_STATUS_BAR_WIDTH_CLASSNAME,
} from "./components/RunStatusBar/RunStatusBar.constants";
import RunStatusTooltip from "./components/RunStatusTooltip/RunStatusTooltip";
import { TOOLTIP_DELAY } from "./RunsStatusBars.constants";
import type { Run, RunsStatusBarsProps } from "./RunsStatusBars.types";
import { aggregateRuns } from "./RunsStatusBars.utils";

const RunsStatusBars = ({
  data,
  pipelineName,
  stretchSpacing,
  className,
  slots = {
    TooltipTitle: RunStatusTooltip,
  },
  slotProps,
}: RunsStatusBarsProps) => {
  const { timeWindow } = useSearchStorage();
  const TooltipTitle = slots.TooltipTitle;
  const buildLink = useBuildLink();

  // if `data` is an array of arrays, aggregate the runs
  data = aggregateRuns(data);

  return (
    <div
      className={cx(
        "runs-status-bars inline-flex gap-[2px]",
        {
          "justify-between w-full": stretchSpacing,
        },
        className
      )}
    >
      {(data as Run[])?.map(({ status, app_pit, last_run, task_id, runs }) => {
        const variant = slotProps?.RunStatusBar?.variant;

        status =
          runs?.length && variant !== "square"
            ? getRunStatusWithRetries(runs)
            : status;

        const to = buildLink(
          task_id
            ? ({
                app_name: pipelineName,
                app_pit: app_pit || "",
                task_id,
                search: { timeWindow },
              } as TaskRunPath)
            : ({
                app_name: pipelineName,
                app_pit: app_pit || "",
                search: { timeWindow },
              } as PitPath)
        );

        return status ? (
          <Link key={app_pit + last_run} relative="path" to={to || "#"}>
            <WhiteTooltip
              arrow
              enterDelay={TOOLTIP_DELAY}
              enterNextDelay={0}
              placement="top"
              title={
                status && TooltipTitle ? (
                  <TooltipTitle {...{ status, app_pit, last_run, runs }} />
                ) : undefined
              }
            >
              <RunStatusBar
                status={status}
                className={cx(slotProps?.RunStatusBar?.className, {
                  "hover:scale-y-125": status,
                })}
                variant={variant}
                isAggregated={variant === "square" && !!runs?.length}
              />
            </WhiteTooltip>
          </Link>
        ) : (
          <div
            key={app_pit + last_run}
            className={cx(
              RUN_STATUS_BAR_WIDTH_CLASSNAME,
              RUN_STATUS_BAR_HEIGHT_CLASSNAME,
              slotProps?.RunStatusBar?.className
            )}
          />
        );
      })}
    </div>
  );
};

export default RunsStatusBars;
